import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  BrainCircuit,
  TrendingUp,
  Building2,
  Settings,
  LogOut,
  User,
  ChevronRight,
  Activity,
  Gauge,
  BarChart2,
} from 'lucide-react';
import { useSiteContext } from '../../contexts/SiteProvider';

const NAV_ITEMS = [
  { icon: LayoutDashboard, label: 'Dashboard',       path: '/dashboard' },
  { icon: Gauge,           label: 'Market Pulse',     path: '/market-pulse',    highlight: true },
  { icon: BrainCircuit,    label: 'AI Trader',        path: '/ai-trader' },
  { icon: BarChart2,       label: 'Trading',          path: '/trading',         highlight: false },
  { icon: TrendingUp,      label: 'Index Dashboard',  path: '/index-dashboard' },
  { icon: Building2,       label: 'Companies',        path: '/company-table' },
  { icon: Settings,        label: 'Settings',         path: '/admin/settings' },
];

const SIDEBAR_W_OPEN  = 210;
const SIDEBAR_W_CLOSE = 58;

export default function Sidebar() {
  const { user, setUser } = useSiteContext();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const h = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);

  const handleLogout = () => {
    setUser(null);
    navigate('/login');
  };

  const w = open ? SIDEBAR_W_OPEN : SIDEBAR_W_CLOSE;

  // Mobile: bottom tab bar
  if (isMobile) {
    return (
      <nav style={{
        position: 'fixed', bottom: 0, left: 0, right: 0,
        height: 54, background: '#07070f',
        borderTop: '1px solid #14141f',
        display: 'flex', zIndex: 300,
      }}>
        {NAV_ITEMS.map(({ icon: Icon, label, path, highlight }) => {
          const active = location.pathname.startsWith(path);
          const color = active ? (highlight ? '#26a65b' : '#4fc3f7') : '#444';
          return (
            <Link key={path} to={path} style={{
              flex: 1, display: 'flex', flexDirection: 'column',
              alignItems: 'center', justifyContent: 'center',
              textDecoration: 'none', gap: 2,
              borderTop: active ? `2px solid ${color}` : '2px solid transparent',
              background: active ? `${color}0a` : 'transparent',
            }}>
              <Icon size={17} color={color} />
              <span style={{ color, fontSize: '0.5rem', fontWeight: active ? 700 : 400 }}>
                {label.split(' ')[0]}
              </span>
            </Link>
          );
        })}
        <button onClick={handleLogout} style={{
          flex: 1, display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center',
          background: 'transparent', border: 'none', cursor: 'pointer', gap: 2,
        }}>
          <LogOut size={17} color="#444" />
          <span style={{ color: '#444', fontSize: '0.5rem' }}>Logout</span>
        </button>
      </nav>
    );
  }

  return (
    <aside
      style={{
        width: w,
        minHeight: '100vh',
        background: '#07070f',
        borderRight: '1px solid #14141f',
        display: 'flex',
        flexDirection: 'column',
        transition: 'width 0.22s ease',
        overflow: 'hidden',
        flexShrink: 0,
        position: 'sticky',
        top: 0,
        zIndex: 200,
      }}
    >
      {/* Logo + toggle */}
      <div
        style={{
          height: 52,
          display: 'flex',
          alignItems: 'center',
          padding: '0 14px',
          gap: 10,
          borderBottom: '1px solid #14141f',
          cursor: 'pointer',
          userSelect: 'none',
        }}
        onClick={() => setOpen(o => !o)}
      >
        <Activity size={20} color="#26a65b" style={{ flexShrink: 0 }} />
        {open && (
          <span style={{ color: '#ddd', fontWeight: 800, fontSize: '0.9rem', letterSpacing: 1, whiteSpace: 'nowrap' }}>
            SDS Trade
          </span>
        )}
        <ChevronRight
          size={14}
          color="#444"
          style={{
            marginLeft: 'auto',
            transform: open ? 'rotate(180deg)' : 'none',
            transition: 'transform 0.22s ease',
            flexShrink: 0,
          }}
        />
      </div>

      {/* Nav items */}
      <nav style={{ flex: 1, padding: '8px 0' }}>
        {NAV_ITEMS.map(({ icon: Icon, label, path, highlight }) => {
          const active = location.pathname.startsWith(path);
          return (
            <Link
              key={path}
              to={path}
              title={label}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                padding: '10px 16px',
                textDecoration: 'none',
                background: active
                  ? highlight ? '#0d2e1a' : '#0f0f1e'
                  : 'transparent',
                borderLeft: active
                  ? `2px solid ${highlight ? '#26a65b' : '#4fc3f7'}`
                  : '2px solid transparent',
                transition: 'background 0.15s',
                whiteSpace: 'nowrap',
              }}
            >
              <Icon
                size={18}
                style={{ flexShrink: 0 }}
                color={
                  active
                    ? highlight ? '#26a65b' : '#4fc3f7'
                    : '#555'
                }
              />
              {open && (
                <span
                  style={{
                    fontSize: '0.8rem',
                    fontWeight: active ? 700 : 400,
                    color: active
                      ? highlight ? '#26a65b' : '#4fc3f7'
                      : '#666',
                  }}
                >
                  {label}
                </span>
              )}
            </Link>
          );
        })}
      </nav>

      {/* User + logout */}
      <div style={{ borderTop: '1px solid #14141f' }}>
        {/* User row */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            padding: '10px 14px',
          }}
        >
          <div
            style={{
              width: 28,
              height: 28,
              borderRadius: '50%',
              background: '#1a3a5c',
              border: '1px solid #2d5a8c',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <User size={14} color="#4fc3f7" />
          </div>
          {open && (
            <span
              style={{
                fontSize: '0.72rem',
                color: '#888',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                maxWidth: 120,
              }}
            >
              {user?.user?.full_name || 'User'}
            </span>
          )}
        </div>

        {/* Logout */}
        <button
          onClick={handleLogout}
          title="Logout"
          style={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            padding: '10px 16px',
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            marginBottom: 6,
            whiteSpace: 'nowrap',
          }}
        >
          <LogOut size={16} color="#555" style={{ flexShrink: 0 }} />
          {open && (
            <span style={{ fontSize: '0.78rem', color: '#555' }}>Logout</span>
          )}
        </button>
      </div>
    </aside>
  );
}
